import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import BreakpointContext from "../../contexts/breakpointContext";
import DraftDispatchContext from "../../contexts/draftDispatchContext";
import DraftStateContext from "../../contexts/draftStateContext";
import useDraftPicks from "../../hooks/queryResults/draft/useDraftPicks";
import useNflTeams from "../../hooks/queryResults/nfl/useNflTeams";
import useAllPlayers from "../../hooks/queryResults/useAllPlayers";
import useDraftMutations from "../../hooks/queryResults/draft/useDraftMutations";
import useIsLoading from "../../hooks/queryResults/useIsLoading";
import useSiteRankings from "../../hooks/queryResults/useSiteRankings";
import useTeams from "../../hooks/queryResults/useTeams";
import useCurrentUser from "../../hooks/useCurrentUser";
import NflUtil from "../../util/nflUtil";
import Util from "../../util/util";
import PlayerImageByPos from "../player/PlayerImageByPos";
import { getPlayerName } from "../player/PlayerName";
import Loading from "../ui/Loading";
import { DRAFT_ACTIONS } from "./draftReducer";
import TeamLogoWithRosterLink from "./TeamLogoWithRosterLink";
import WatchIcon from "./WatchIcon";
import styled from "styled-components";
import classNames from "classnames";

const StyledTable = styled.table`
  margin: 0 auto;

  & tr {
    cursor: pointer;
  }

  & td,
  & th {
    padding: 0.25rem 0.75rem;
    height: 38px;
  }

  & tr.taken,
  & tr.taken:hover td {
    background-color: #ddd;
    color: #aaa;
  }

  & tr.taken:nth-child(odd),
  & tr.taken:nth-child(odd):hover {
    background-color: #ccc;
    color: #888;
  }

  & tr.selected td,
  & tr.selected:hover td {
    background-color: #0078f8;
    color: white;
  }

  & tr.selected.on-clock td,
  & tr.selected.on-clock:hover td {
    background-color: #00ab00;
    color: white;
  }

  & tr.selected.on-clock.time-running-out td,
  & tr.selected.on-clock.time-running-out:hover td {
    background-color: #ff0000;
    color: white;
  }
`;

export default function PlayersTable() {
  const breakpoints = useContext(BreakpointContext);
  const draftState = useContext(DraftStateContext);
  const draftDispatch = useContext(DraftDispatchContext);
  const currentUser = useCurrentUser();

  const { selectedPlayerId, watchList } = draftState;
  const { teamIdOnClock, timeIsRunningOut } = draftState.info;

  const { playersData, playersQuery } = useAllPlayers();
  const { siteRankingsData, siteRankingsQuery } = useSiteRankings();
  const { draftPicksData, draftPicksQuery } = useDraftPicks();
  const { teams, teamsQuery } = useTeams();
  const { nflTeamsData, nflTeamsQuery } = useNflTeams();
  const { updateWatchListMutation } = useDraftMutations();

  const entries = siteRankingsData ? siteRankingsData.playerList.entries : {};
  const entriesAsString = JSON.stringify(entries);

  const takenPlayers = draftPicksData ? draftPicksData.players : {};
  const takenPlayersAsString = JSON.stringify(takenPlayers);

  const players = playersData ? playersData.players : {};
  const playersAsString = JSON.stringify(players);

  const tick = 150;
  const [numToShow, setNumToShow] = useState(tick);
  const [isShowingAll, setIsShowingAll] = useState(false);
  const [filteredEntries, setFilteredEntries] = useState({});
  const [isSearchFilterOn, setIsSearchFilterOn] = useState(false);
  const [hideDraftedPlayers, setHideDraftedPlayers] = useState(true);
  const [posFilter, setPosFilter] = useState("all");
  const searchRef = useRef();

  const isLoading = useIsLoading(
    draftPicksData,
    draftPicksQuery,
    nflTeamsData,
    nflTeamsQuery,
    playersData,
    playersQuery,
    siteRankingsData,
    siteRankingsQuery,
    teams,
    teamsQuery
  );

  const getEntriesToShow = useCallback(
    (entries) => {
      let takenPlayers = null;
      if (hideDraftedPlayers) takenPlayers = JSON.parse(takenPlayersAsString);

      let players = null;
      if (posFilter !== "all" || isSearchFilterOn)
        players = JSON.parse(playersAsString);

      let pattern = new RegExp("abc");
      if (isSearchFilterOn) {
        const searchString = searchRef.current.value
          .replace(/\s*/g, "")
          .toLowerCase()
          .trim();
        console.log("search filter", searchString);
        pattern = new RegExp(searchString, "i");
      }

      const newEntries = {};
      Object.keys(entries)
        .filter((rank) => {
          let keep = true;
          const playerId = entries[rank];
          if (hideDraftedPlayers) {
            const isTaken = takenPlayers.hasOwnProperty(playerId);
            keep = !isTaken;
          }

          if (keep && posFilter !== "all") {
            const player = players[playerId];
            keep = player.pos === posFilter;
          }

          if (keep && isSearchFilterOn) {
            const player = players[playerId];
            const { firstName, lastName } = player;
            let checkString = `${firstName}${lastName}`
              .replace(/\s*/g, "")
              .trim();
            if (player.nflTeamAbbr) checkString += player.nflTeamAbbr;
            keep = pattern.test(checkString);
          }

          return keep;
        })
        .slice(0, numToShow)
        .forEach((rank) => (newEntries[rank] = entries[rank]));
      return newEntries;
    },
    [
      numToShow,
      hideDraftedPlayers,
      takenPlayersAsString,
      posFilter,
      playersAsString,
      isSearchFilterOn,
    ]
  );

  useEffect(() => {
    setFilteredEntries(getEntriesToShow(JSON.parse(entriesAsString)));
  }, [entriesAsString, getEntriesToShow]);

  if (isLoading) return <Loading />;

  const toggleShowMore = () => {
    const numEntries = siteRankingsData
      ? Object.keys(siteRankingsData.playerList.entries).length
      : 0;

    const newNumToShow = numToShow >= numEntries ? tick : numToShow + tick;
    setNumToShow(newNumToShow);
    setIsShowingAll(newNumToShow >= numEntries);
  };

  const teamOnClock = teamIdOnClock ? teams.getTeamById(teamIdOnClock) : null;
  let userIsOnClock = Util.hasDraftManagerRole(currentUser, teamOnClock);

  const handlePlayerSearch = () => {
    const searchIsOn = searchRef.current.value.length >= 3;
    setIsSearchFilterOn(searchIsOn);
    setHideDraftedPlayers(!searchIsOn);
    setFilteredEntries(getEntriesToShow(entries));
  };

  const watchListPlayerIsTaken = (playerId) => {
    return draftPicksData.players.hasOwnProperty(playerId);
  };

  const positionFilter = (
    <>
      Pos
      <br />
      <select
        onChange={(event) => setPosFilter(event.target.value)}
        value={posFilter}
      >
        <option value={"all"}>All</option>
        <option value={"QB"}>QB</option>
        <option value={"RB"}>RB</option>
        <option value={"WR"}>WR</option>
        <option value={"TE"}>TE</option>
        <option value={"K"}>K</option>
        <option value={"DEF"}>DEF</option>
      </select>
    </>
  );

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          margin: "0 auto",
          padding: "0 0.5rem",
        }}
      >
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginRight: "0.25rem",
            }}
          >
            Hide Taken Players
            <input
              type={"checkbox"}
              checked={hideDraftedPlayers}
              onChange={() => setHideDraftedPlayers(!hideDraftedPlayers)}
              style={{ marginLeft: "0.25rem" }}
            />
          </div>
        </div>
        <input
          ref={searchRef}
          type={"text"}
          placeholder={"Search players..."}
          onChange={handlePlayerSearch}
          style={{ width: breakpoints.sm ? "16rem" : "12rem" }}
        />
      </div>
      <StyledTable>
        <thead>
          <tr>
            <th>{breakpoints.sm ? "Watch" : ""}</th>
            <th title={"Rank"}>Rk</th>
            {hideDraftedPlayers ? null : <th title={"Fantasy Team"}>Tm</th>}
            {breakpoints.sm ? (
              <>
                {breakpoints.lg ? <th>&nbsp;</th> : null}
                <th title={"Position"}>{positionFilter}</th>
                <th>Name</th>
                <th title={"NFL Team"}>NFL</th>
                <th>Bye</th>
              </>
            ) : (
              <th>{positionFilter}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {Object.keys(filteredEntries).length ? null : (
            <tr>
              <td colSpan={8}>No players found.</td>
            </tr>
          )}
          {Object.keys(filteredEntries).map((rank) => {
            const playerId = filteredEntries[rank];
            const player = players[playerId];

            let pos = null;
            let nflTeamAbbr = null;
            if (player) ({ pos, nflTeamAbbr } = player);
            const formattedNflTeamAbbr = NflUtil.formatNflTeamAbbr(nflTeamAbbr);

            const classNamesObj = {};

            let isAvailable = true;
            let teamLogo = null;
            const isTaken = takenPlayers.hasOwnProperty("" + playerId);

            if (isTaken) {
              // player is taken
              const pick = takenPlayers[playerId];
              const teamId = pick.team;
              let team = null;
              if (teamId) team = teams.getTeamById(teamId, false);
              if (team) {
                teamLogo = <TeamLogoWithRosterLink team={team} size={"sm"} />;
                classNamesObj.taken = true;
                isAvailable = false;
              }
            }

            let byeWeek = null;
            let nflTeam;
            if (nflTeamAbbr) {
              nflTeam = nflTeamsData.nflTeams[nflTeamAbbr];
              if (nflTeam) byeWeek = nflTeam.byeWeek;
            }

            const isSelected = selectedPlayerId === playerId;
            if (isSelected) {
              classNamesObj.selected = isSelected;
              classNamesObj["on-clock"] = userIsOnClock;
              classNamesObj["time-running-out"] =
                userIsOnClock && timeIsRunningOut;
            }

            let isWatching = watchList.includes(playerId);

            const toggleWatching = (event) => {
              event.stopPropagation();
              let newWatchList;
              if (isWatching) {
                draftDispatch({
                  type: DRAFT_ACTIONS.REMOVE_FROM_WATCH_LIST,
                  payload: playerId,
                });
                newWatchList = watchList.filter((pid) => pid !== playerId);
              } else {
                draftDispatch({
                  type: DRAFT_ACTIONS.ADD_TO_WATCH_LIST,
                  payload: playerId,
                });
                newWatchList = [...watchList, playerId];
              }
              isWatching = !isWatching;

              newWatchList = newWatchList.filter(
                (playerId) => !watchListPlayerIsTaken(playerId)
              );
              updateWatchListMutation.mutate(newWatchList);
            };

            const handleClick = () => {
              if (isAvailable)
                draftDispatch({
                  type: DRAFT_ACTIONS.SELECT_PLAYER_ID,
                  payload: playerId,
                });
            };

            return (
              <tr
                key={playerId}
                className={classNames(classNamesObj)}
                onClick={handleClick}
              >
                <td>
                  {!isTaken ? (
                    <WatchIcon
                      isWatching={isWatching}
                      toggleWatching={toggleWatching}
                      isSelected={isSelected}
                    />
                  ) : null}
                </td>
                <td>{rank}</td>
                {hideDraftedPlayers ? null : <td>{teamLogo}</td>}
                {breakpoints.sm ? (
                  <>
                    {breakpoints.lg ? (
                      <td>
                        <PlayerImageByPos
                          player={player}
                          style={{ maxWidth: "18px" }}
                        />
                      </td>
                    ) : null}
                    <td>{pos}</td>
                    <td className={"left"}>
                      {getPlayerName(player, { injury: 1 })}
                    </td>
                    <td>{formattedNflTeamAbbr}</td>
                    <td>{byeWeek}</td>
                  </>
                ) : (
                  <td className={"left"}>
                    {getPlayerName(player, {
                      pos: 1,
                      short: 1,
                      nfl: 1,
                      injury: 1,
                      bye: byeWeek,
                    })}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </StyledTable>
      {!isSearchFilterOn && (
        <p
          onClick={toggleShowMore}
          style={{ width: "100%", textAlign: "center", cursor: "pointer" }}
        >
          Show {isShowingAll ? "less" : "more"} players
        </p>
      )}
    </div>
  );
}
